import { standardDataGridColumns } from './constants';
import {
  enqueueSuccessSnackbar,
  enqueueWarningSnackbar,
} from '../store/notifications/actionWrapper';
import { Dispatch } from 'react';
import { Project, ProjectStatus, TableProject } from '../models/project';
import { resources } from './translation';
import { TType } from '../hooks/useTranslation';
import {
  PROJECT_STATUS_NOT_STARTED,
  PROJECT_STATUS_UNQUALIFIED,
  STATUSES_OBJ,
} from './statuses/constants';
import { getFY } from './calculation';
import getSymbolFromCurrency from 'currency-symbol-map';
import { CountryType } from '../models/countryUnitActuals';
import { unknownObject } from '../models/unkrnownObject';
import { AD, roleRestricted, SA } from '../components/RoleRestricted/utils';
import { projectFromReltp, isPartOfCrpPlan } from './fieldEditingRules';

export const removeReplaceAssets = (assets) =>
  assets?.filter((row) => !row.assetNum);

export const getUniqueKey = (): number => new Date().getTime() + Math.random();

export const getCookie = (name: string) => {
  const cookie = {};
  if (typeof window !== 'undefined') {
    window.document.cookie.split(';').forEach(function (el) {
      const [k, v] = el.split('=');
      cookie[k.trim()] = v;
    });
  }
  return cookie[name];
};

export const getTableVisibilityColumns = (columns) => ({
  ...standardDataGridColumns,
  ...columns,
});

export const copyToClipboard = ({
  textToCopy,
  dispatch,
  event,
  textToDisplay,
}: {
  textToCopy: string;
  event?: Event;
  dispatch: Dispatch<unknown>;
  textToDisplay: string;
}) => {
  event?.preventDefault();
  navigator.clipboard.writeText(textToCopy).then(() => {
    dispatch(enqueueSuccessSnackbar(`${textToDisplay} copied to clipboard`));
  });
};

export const getCountryPropertyFromBU = (
  BUArray,
  buInputValue,
  field = 'countryName',
) => {
  const BU = BUArray?.find((bus) => bus.businessUnitId === buInputValue);
  return BU?.[field];
};

export const getProjectsToSendArchive = (
  allProjectsData: Project[],
  projects: string[],
  dispatch: Dispatch<unknown>,
) => {
  const filterProjectsForArchive = allProjectsData.filter(
    (p) => projects.includes(p.id) && !p.mtoolId,
  );
  const archivedProject = filterProjectsForArchive.map((p) => p?.id);

  if (archivedProject.length < projects.length) {
    dispatch(
      enqueueWarningSnackbar(
        resources.en.snackbarMessages.problemToAddArchived,
      ),
    );
  }
  return archivedProject;
};

export const checkIfProjectsCanBeDeleted = (projects, selection) => {
  const filterDraftProject = projects.filter(
    (p) =>
      selection.includes(p.id) &&
      p.projectStatus === STATUSES_OBJ[PROJECT_STATUS_UNQUALIFIED],
  );

  return selection.length === filterDraftProject.length;
};

export const findCurrencyByBu = (BUs, selectedBU, currencies) => {
  const years = Object.keys(currencies);
  const year = years?.includes(getFY()) ? getFY() : years[0];
  const BU = BUs.find(
    (businessUnit) => businessUnit.businessUnitId == selectedBU,
  );
  if (!BU) return 'EUR';
  const currency = currencies?.[year]?.find(
    (currency) => currency.countryId == BU.countryId,
  );
  return currency?.currencyCode || 'EUR';
};

export const getCurrencyCodeFromProjectBu = (BUs, bu) =>
  BUs?.find((b) => b.businessUnitId === bu)?.currencyCode || 'EUR';

export const touchField = (setValues) =>
  setValues('hasBeenTouched', '', { shouldTouch: true });

export const getGeneralFormDefaultValue = (selectedBUs, BUs) => {
  return (
    (Array.isArray(selectedBUs) ? selectedBUs[0] : selectedBUs) ||
    BUs[0]?.businessUnitId
  );
};

export const isAddtionalApproval = (p) =>
  p?.projectStatus === STATUSES_OBJ[PROJECT_STATUS_NOT_STARTED] ||
  p?.projectStatus === STATUSES_OBJ[PROJECT_STATUS_NOT_STARTED];

export const getSelectOptions = (key: string, t: TType, value?) => {
  const object = t(key);
  const values = Object.keys(object);
  const defaultOptions = [];
  values.forEach((key) => {
    if (key !== value && object[key].includes('[LEGACY]')) return;
    defaultOptions.push({
      value: key,
      label: object[key],
      disabled: object[key]?.includes('[LEGACY]'),
    });
  });
  if (value && !values.includes(value))
    defaultOptions.push({ value, label: value, disabled: true });

  return defaultOptions;
};

export const dispayLabel = ({ value, field, api }) => {
  const colDef = api.getColumn(field);
  const option = colDef.valueOptions.find(
    ({ value: optionValue }) => value === optionValue,
  );

  return option?.label;
};

export const removeOffset = (date) => {
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - offset * 60 * 1000);
  return date.toISOString().split('T')[0];
};

export const trimDate = (date: Date | string) =>
  date instanceof Date ? removeOffset(date) : date;

export const trimDatesInProject = (project) => {
  const startDate = project.projectStartDate;
  const endDate = project.projectCompletionDate;
  return {
    ...project,
    projectStartDate: trimDate(startDate),
    projectCompletionDate: trimDate(endDate),
  };
};

export const returnCurrencySymbol = (currencyCode) =>
  currencyCode ? getSymbolFromCurrency(currencyCode) : '';

export const getTotalInvestmentsSum = (
  land: number,
  propertyInvestment: number,
  retailCost: number,
  retailInvestment: number,
) => {
  if (propertyInvestment) {
    return propertyInvestment + retailCost + retailInvestment;
  } else {
    return land + retailCost + retailInvestment;
  }
};

export const getTertial = (date: Date) => {
  let tertial = 1;
  const month = new Date(date).getMonth() + 1;
  if (month >= 1 && month <= 4) {
    tertial = 2;
  }
  if (month >= 5 && month <= 8) {
    tertial = 3;
  }
  return tertial;
};

export const getProjectsToFetch = (
  countryCodes: string[],
  countyList: CountryType[],
) => {
  let countriesToFetch = [];
  if (countryCodes.includes('All')) {
    countriesToFetch = countyList.map((el) => el.countryId);
  } else {
    countriesToFetch = countryCodes.map(
      (el) =>
        countyList.find((countryItem) => countryItem.countryName === el)
          .countryId,
    );
  }

  return countriesToFetch;
};

export const getOptionsFromTranslation = (t: TType, key: string) =>
  t(key) as unknown as unknownObject;

const getOptionsFromStatus = (
  status: ProjectStatus,
  isep: boolean,
  partOfCRP: boolean,
  isCountryAdmin: boolean,
  isSuperAdmin: boolean,
  partOfCrpPlan: boolean,
) => {
  switch (status) {
    case ProjectStatus.NOT_STARTED:
      return [
        ProjectStatus.RUNNING,
        ProjectStatus.DELIVERED,
        ProjectStatus.CLOSED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ON_HOLD,
      ];
    case ProjectStatus.DRAFT: {
      const baseArray = [
        ProjectStatus.UNQUALIFIED,

        ProjectStatus.CANCELLED,
        ProjectStatus.ON_HOLD,
      ];
      return isep && isSuperAdmin
        ? [
            ...baseArray,
            ProjectStatus.NOT_STARTED, //  ISEP is set to Yes by Super Admin)
          ]
        : baseArray;
    }
    case ProjectStatus.RUNNING:
      return [
        ProjectStatus.NOT_STARTED,
        ProjectStatus.DELIVERED,
        ProjectStatus.CLOSED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ON_HOLD,
      ];
    case ProjectStatus.QUALIFIED: {
      const baseQualifiedStatuses = [
        ProjectStatus.UNQUALIFIED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ON_HOLD,
      ];
      return [...baseQualifiedStatuses, ProjectStatus.DRAFT];
    }
    case ProjectStatus.UNQUALIFIED: {
      const baseStatuses = [
        ProjectStatus.QUALIFIED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ON_HOLD,
      ];
      return partOfCrpPlan
        ? [...baseStatuses, ProjectStatus.DRAFT]
        : baseStatuses;
    }
    case ProjectStatus.CANCELLED:
      return [ProjectStatus.ON_HOLD];
    case ProjectStatus.ON_HOLD: {
      const baseArray = [ProjectStatus.CANCELLED];
      return isep
        ? [
            ...baseArray,
            ...[
              ProjectStatus.NOT_STARTED,
              ProjectStatus.RUNNING,
              ProjectStatus.DELIVERED,
              ProjectStatus.CLOSED,
            ],
          ]
        : [...baseArray, ProjectStatus.UNQUALIFIED];
    }
    case ProjectStatus.DELIVERED:
      return [
        ProjectStatus.NOT_STARTED,
        ProjectStatus.RUNNING,
        ProjectStatus.CLOSED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ON_HOLD,
      ];
    case ProjectStatus.CLOSED:
      return [
        ProjectStatus.NOT_STARTED,
        ProjectStatus.RUNNING,
        ProjectStatus.DELIVERED,
        ProjectStatus.CANCELLED,
        ProjectStatus.ON_HOLD,
      ];
  }
};

const statusJourneyOrder = [
  ProjectStatus.QUALIFIED,
  ProjectStatus.UNQUALIFIED,
  ProjectStatus.DRAFT,
  ProjectStatus.NOT_STARTED,
  ProjectStatus.RUNNING,
  ProjectStatus.DELIVERED,
  ProjectStatus.CLOSED,
  ProjectStatus.CANCELLED,
  ProjectStatus.ON_HOLD,
];

export const sortStatusesByStatusJourney = (statuses) => {
  return statuses?.sort(
    (a, b) => statusJourneyOrder.indexOf(a) - statusJourneyOrder.indexOf(b),
  );
};

export const returnAllowedStatuses = (
  projects: TableProject[],
  role: string,
) => {
  const reltpProject = projects.find((project) => projectFromReltp(project));
  if (reltpProject) return [reltpProject.projectStatus];
  if (roleRestricted([SA], role)) return statusJourneyOrder;
  const overallISEP = projects.every((project) => project?.isep);
  const overallPartOfCrp = projects.every((project) => project?.partOfOMD);
  const overallPartOfCrpPlan = projects.every((project) =>
    isPartOfCrpPlan(project?.partOfCrp),
  );

  const uniqueStatuses = [
    ...new Set(projects.map((project) => project.projectStatus)),
  ];

  return sortStatusesByStatusJourney(
    uniqueStatuses
      .map((status) =>
        getOptionsFromStatus(
          status,
          overallISEP,
          overallPartOfCrp,
          roleRestricted([AD, SA], role),
          roleRestricted([SA], role),
          overallPartOfCrpPlan,
        ),
      )
      .reduce((acc, currentArray) =>
        acc.filter((item) => currentArray.includes(item)),
      ),
  );
};

export const getIdFromLabel = (label: string) =>
  label.split(' ').join('-').toLowerCase();
